/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorComponent from '../ErrorComponent'

interface FederationErrorFallbackProps {
  error: Error
  resetErrorBoundary: () => void
}

const FederationErrorFallback: React.FC<FederationErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  console.error('Federation Error:', error)

  // Check if it's a React context error
  if (isReactContextError(error)) {
    return (
      <div className="alert alert-error" role="alert">
        <div>
          <h3>Module Federation Error</h3>
          <p>There was an issue loading the remote module. This might be due to a React context sharing problem.</p>
          <div className="alert-actions">
            <button className="btn btn-primary" onClick={resetErrorBoundary}>
              Try Again
            </button>
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>
              Reload Page
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <ErrorComponent />
}

interface FederationErrorBoundaryProps {
  children: React.ReactNode
}

export const FederationErrorBoundary: React.FC<FederationErrorBoundaryProps> = ({ children }) => {
  return (
    <ErrorBoundary
      FallbackComponent={FederationErrorFallback}
      onError={(error, errorInfo) => {
        console.error('Federation ErrorBoundary caught an error:', error, errorInfo)
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

const isReactContextError = (error: Error): boolean => {
  // Check if the error message contains "React context"
  return (
    error.message.includes('React context') ||
    error.message.includes('Context not found') ||
    error.message.includes('useContext') ||
    error.message.includes('No context provider found')
  )
}

export default FederationErrorBoundary
